//src/pages/AppointmentDetails.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Box,
  Text,
  Button,
  AccordionPanel,
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
} from "@chakra-ui/react";
import { useLocation, useParams } from "react-router-dom";
import SubHeading from "../components/typography/SubHeading";
import DesktopHeader from "../components/defaultHeader/DesktopHeader";
import Heading from "../components/typography/Heading";
import colors from "../utils/colors";
import {
  useGetAppointmentComplaint,
  useGetPatientDetails,
  useGetAppointmentFamilyHistory,
  useGetAppointmentSocialHistory,
  useGetAppointmentPastMedicalHistory,
  useGetAppointmentMedicationHistory,
  useGetMediaByAppointment,
  useGetProvisionalDifferentialDiagnosis,
  useGetDefinitiveDiagnosis,
  useGetTreatmentsByAppointmentId,
  useGetPhysicalFindingsNotes,
  useGetOrderedTestsByAppointment,
} from "../apis/patients";
import { useGetDoctorNamesByIds } from "../apis/doctor";
import PatientAdmissionCard from "../features/admissions/PatientAdmissionCard";
import { buildAdmissionSeedFromAppointment } from "../utils/admissionSeed";

const AppointmentDetails: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [modalContent, setModalContent] = useState<{
    title: string;
    data: string | JSX.Element | null;
  }>({
    title: "Check the content",
    data: "",
  });

  const location = useLocation();

  // Patient id is in URL: /appointments/:patientId/:appointmentId or similar
  const patientIdFromRoute = useMemo(
    () => location?.pathname?.split("/")?.[2] ?? "",
    [location?.pathname],
  );

  // ---- Data fetching ----
  const { data: patientDetails } = useGetPatientDetails({
    patientId: patientIdFromRoute,
  });

  const { data: complaintsData } = useGetAppointmentComplaint(
    appointmentId || "",
  );
  const { data: pastMedicalHistory } = useGetAppointmentPastMedicalHistory(
    appointmentId || "",
  );
  const { data: familyHistory } = useGetAppointmentFamilyHistory(
    appointmentId || "",
  );
  const { data: socialHistory } = useGetAppointmentSocialHistory(
    appointmentId || "",
  );
  const { data: medicationHistory } = useGetAppointmentMedicationHistory(
    appointmentId || "",
  );
  const { data: mediaData } = useGetMediaByAppointment(appointmentId || "");
  const { data: provisionalDiagnosisData } =
    useGetProvisionalDifferentialDiagnosis(appointmentId || "");
  const { data: definitiveDiagnosisData } = useGetDefinitiveDiagnosis(
    appointmentId || "",
  );
  const { data: treatmentData } = useGetTreatmentsByAppointmentId(
    appointmentId || "",
  );
  const { data: physicalFindingsData } = useGetPhysicalFindingsNotes(
    appointmentId || "",
  );
  const { data: orderedTestsData } = useGetOrderedTestsByAppointment(
    appointmentId || "",
  );

  const referralData = patientDetails?.data?.referredTo || [];
  const { data: doctorList } = useGetDoctorNamesByIds(referralData);

  const [patientData, setPatientData] = useState<Record<string, any[]>>({
    "Past Medical History": [],
    "Family History": [],
    "Social History": [],
    "Medication History": [],
    "Physical Findings": [],
    Media: [],
    "Differential Diagnosis": [],
    Tests: [],
    "Definitive Diagnosis": [],
    Treatment: [],
    "Refer Patient": [],
  });

  // Normalize histories into buckets for the modal views
  useEffect(() => {
    const flattenHistories = (histories: any[] | undefined, key: string) =>
      histories?.flatMap((item) => item[key] || []) || [];

    setPatientData({
      "Past Medical History": flattenHistories(
        pastMedicalHistory,
        "pastMedicalHistories",
      ),
      "Family History": flattenHistories(familyHistory, "familyHistories"),
      "Social History": flattenHistories(socialHistory, "socialHistories"),
      "Medication History": medicationHistory?.medications || [],
      "Physical Findings": physicalFindingsData || [],
      Media: mediaData?.data || [],
      "Differential Diagnosis": provisionalDiagnosisData?.data?.history || [],
      Tests: orderedTestsData || [],
      "Definitive Diagnosis": definitiveDiagnosisData?.data
        ? [{ notes: definitiveDiagnosisData.data }]
        : [],
      Treatment: treatmentData || [],
      "Refer Patient": referralData ? referralData : [],
    });
  }, [
    pastMedicalHistory,
    familyHistory,
    socialHistory,
    medicationHistory,
    mediaData,
    provisionalDiagnosisData,
    definitiveDiagnosisData,
    treatmentData,
    physicalFindingsData,
    orderedTestsData,
    referralData,
  ]);

  const seedText = useMemo(() => {
    // 1) Build the rich seed based on the appointment context
    const rawSeed = buildAdmissionSeedFromAppointment({
      patientName: patientDetails?.data?.name,
      patientId: patientIdFromRoute,
      appointmentId: appointmentId || "",
      admittedAt: new Date(),

      // full appointment details
      complaintsData,
      physicalFindingsData,
      provisionalDiagnosisData,
      definitiveDiagnosisData,
      treatmentData,
      orderedTestsData,
      pastMedicalHistory,
      familyHistory,
      socialHistory,
      medicationHistory,
      mediaData,
      referralData: doctorList,
    });

    // 2) Fallback — ensure seed is NEVER empty
    const fallback =
      `Preliminary admission notes for patient ` +
      `${patientDetails?.data?.name ?? "Unknown"} ` +
      `(${patientIdFromRoute || "No ID"}) at ${new Date().toLocaleString()}`;

    return rawSeed && rawSeed.trim().length > 0 ? rawSeed : fallback;
  }, [
    patientDetails?.data?.name,
    patientIdFromRoute,
    appointmentId,
    complaintsData,
    physicalFindingsData,
    provisionalDiagnosisData,
    definitiveDiagnosisData,
    treatmentData,
    orderedTestsData,
    pastMedicalHistory,
    familyHistory,
    socialHistory,
    medicationHistory,
    mediaData,
    doctorList,
  ]);

  // ---- Modal open helper ----
  const openModal = (title: string) => {
    let dataList;

    if (title === "Main Complaint") {
      dataList = complaintsData?.mainComplaint
        ? [complaintsData.mainComplaint]
        : [];
    } else if (title === "Other Complaints") {
      dataList = complaintsData?.otherComplaints || [];
    } else {
      dataList = patientData[title] || [];
    }

    if (!dataList || dataList.length === 0) {
      setModalContent({ title, data: "No records found." });
      onOpen();
      return;
    }

    const modalJSX = (() => {
      switch (title) {
        case "Main Complaint":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box key={i} mb={4}>
                  <Text>
                    <b>Complaint:</b> {item.mainComplaint || "Check contents"}
                  </Text>
                  {item.cond && (
                    <Text>
                      <b>Condition:</b> {item.cond}
                    </Text>
                  )}
                  {item.mainComplaintNote && (
                    <Text>
                      <b>Note:</b> {item.mainComplaintNote}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          );

        case "Other Complaints":
          return (
            <Accordion allowToggle>
              {dataList.map((item: any, i: number) => (
                <AccordionItem key={i}>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="medium">
                          {item.complaint || "Check contents"}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    {item.note && (
                      <Text color="blue.600">
                        <b>Note:</b> {item.note}
                      </Text>
                    )}
                    {item.cond && (
                      <Text mt={2}>
                        <b>Condition:</b> {item.cond}
                      </Text>
                    )}
                    {!item.note && !item.cond && (
                      <Text fontStyle="italic" color="gray.500">
                        No additional details.
                      </Text>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          );

        case "Past Medical History":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text mb={1}>
                    <b>Category:</b> {item.category || "N/A"}
                  </Text>
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Family History":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text mb={1}>
                    <b>Category:</b> {item.category || "N/A"}
                  </Text>
                  <Text mb={1}>
                    <b>Family Member Affected:</b>{" "}
                    {item.familyMemberAffected || "N/A"}
                  </Text>
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Social History":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text mb={1}>
                    <b>Habits:</b> {item.habits || "N/A"}
                  </Text>
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Medication History":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text mb={1}>
                    <b>Name:</b> {item.name || "N/A"}
                  </Text>
                  {item.route && (
                    <Text mb={1}>
                      <b>Route:</b> {item.route}
                    </Text>
                  )}
                  {item.dosage && (
                    <Text mb={1}>
                      <b>Dosage:</b> {item.dosage}
                    </Text>
                  )}
                  {item.frequency && (
                    <Text mb={1}>
                      <b>Frequency:</b> {item.frequency}
                    </Text>
                  )}
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Physical Findings":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text fontWeight="bold">{item.title || "Finding"}:</Text>
                  <Text>{item.content || "N/A"}</Text>
                </Box>
              ))}
            </Box>
          );

        case "Media":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box key={i} mb={4} borderBottom="1px solid #eee" pb={4}>
                  <Text mb={2}>
                    <b>Label:</b> {item.label || item.tag || "Unnamed Media"}
                  </Text>

                  {/* {item.tag && (
                    <Text mb={2}>
                      <b>Tag:</b> {item.tag}
                    </Text>
                  )} */}

                  {item.url ? (
                    <Button
                      as="a"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      colorScheme="blue"
                    >
                      View Media
                    </Button>
                  ) : (
                    <Text fontStyle="italic" color="gray.500">
                      No URL provided
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          );

        case "Differential Diagnosis":
          return (
            <Box>
              {dataList.map((historyEntry: any, i: number) => (
                <Box
                  key={historyEntry._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  {historyEntry.provisionalDiagnosis && (
                    <Box
                      mb={historyEntry.differentialDiagnosis?.length ? 2 : 0}
                    >
                      <Text fontWeight="bold">Provisional Diagnosis:</Text>
                      <Text>{historyEntry.provisionalDiagnosis}</Text>
                    </Box>
                  )}

                  {historyEntry.differentialDiagnosis &&
                    historyEntry.differentialDiagnosis.length > 0 && (
                      <Box>
                        <Text fontWeight="bold">Differential Diagnoses:</Text>
                        {historyEntry.differentialDiagnosis.map(
                          (diag: string, idx: number) => (
                            <Text key={idx} ml={4}>
                              - {diag}
                            </Text>
                          ),
                        )}
                      </Box>
                    )}

                  {!historyEntry.provisionalDiagnosis &&
                    (!historyEntry.differentialDiagnosis ||
                      historyEntry.differentialDiagnosis.length === 0) && (
                      <Text fontStyle="italic" color="gray.500">
                        No diagnosis recorded for this entry.
                      </Text>
                    )}
                </Box>
              ))}
            </Box>
          );

        case "Tests":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text fontWeight="bold">Test Name:</Text>
                  <Text mb={2}>{item.name || "N/A"}</Text>
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Definitive Diagnosis":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box key={i} mb={4} borderBottom="1px solid #eee" pb={4}>
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Treatment":
          return (
            <Box>
              {dataList.map((item: any, i: number) => (
                <Box
                  key={item._id || i}
                  mb={4}
                  borderBottom="1px solid #eee"
                  pb={4}
                >
                  <Text mb={1}>
                    <b>Name:</b> {item.name || "N/A"}
                  </Text>
                  {item.route && (
                    <Text mb={1}>
                      <b>Route:</b> {item.route}
                    </Text>
                  )}
                  {item.dosage && (
                    <Text mb={1}>
                      <b>Dosage:</b> {item.dosage}
                    </Text>
                  )}
                  {item.frequency && (
                    <Text mb={1}>
                      <b>Frequency:</b> {item.frequency}
                    </Text>
                  )}
                  {item.duration && (
                    <Text mb={1}>
                      <b>Duration:</b> {item.duration}
                    </Text>
                  )}
                  <Text>
                    <b>Notes:</b> {item.notes || "N/A"}
                  </Text>
                </Box>
              ))}
            </Box>
          );

        case "Refer Patient":
          return (
            <Box>
              <Text fontWeight="bold" mb={2}>
                Referred To:
              </Text>
              {doctorList?.length ? (
                <Box as="ul" pl={4}>
                  {doctorList.map((doc: any, index: number) => (
                    <li key={index}>
                      <Text>{doc.name || doc.email || doc._id}</Text>
                    </li>
                  ))}
                </Box>
              ) : (
                <Text fontStyle="italic" color="gray.500">
                  No referral information available.
                </Text>
              )}
            </Box>
          );

        default:
          return <Text>No content available for this section.</Text>;
      }
    })();

    setModalContent({ title, data: modalJSX });
    onOpen();
  };

  return (
    <>
      <DesktopHeader
        headerContent={
          <Flex alignItems="center" gap={"12px"}>
            <Heading color={colors.grey}>Appointments</Heading>
            <Heading color={colors.grey}>{`>`}</Heading>
            <Heading>{patientDetails?.data?.name || "Patient Name"}</Heading>
          </Flex>
        }
      />

      <Flex
        px={[4, 6, 10]}
        py={[4, 6]}
        w="100%"
        minH="100vh"
        bg={colors.lightGrey}
        direction="column"
      >
        <SubHeading size="s">Patient Appointment Records</SubHeading>

        <Flex
          direction="column"
          bg="white"
          borderRadius="8px"
          boxShadow="md"
          w="100%"
          px={[4, 6, 8]}
          py={[4, 6]}
          mt={6}
          gap={6}
        >
          {/* Admission banner + admit/discharge + wiring seed into admission flow */}
          <PatientAdmissionCard
            patientId={patientIdFromRoute}
            appointmentId={appointmentId}
            seedText={seedText}
          />

          <Box mb={6}>
            <Text fontWeight={600}>Main Complaint:</Text>
            {complaintsData?.mainComplaint ? (
              <Box>
                <Text>{complaintsData.mainComplaint.mainComplaint}</Text>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                  mt={2}
                  onClick={() => openModal("Main Complaint")}
                >
                  View Details
                </Button>
              </Box>
            ) : (
              <Text fontStyle="italic" color="gray.500">
                No main complaint recorded.
              </Text>
            )}
          </Box>

          <Box>
            <Text fontWeight={600}>Other Complaints:</Text>
            {complaintsData?.otherComplaints?.length ? (
              <Box>
                <Button
                  size="sm"
                  colorScheme="teal"
                  mt={2}
                  onClick={() => openModal("Other Complaints")}
                >
                  View Details
                </Button>
              </Box>
            ) : (
              <Text fontStyle="italic" color="gray.500">
                No other complaints recorded.
              </Text>
            )}
          </Box>

          <Flex gap={4} flexWrap="wrap" justify="space-between">
            {[
              "Past Medical History",
              "Family History",
              "Social History",
              "Medication History",
              "Physical Findings",
              "Media",
              "Differential Diagnosis",
              "Tests",
              "Definitive Diagnosis",
              "Treatment",
              "Refer Patient",
            ].map((title) => (
              <Box flex="1 1 100%" minW={["100%", "45%", "250px"]} key={title}>
                <Text fontWeight={600}>{title}:</Text>
                <Button
                  size="sm"
                  colorScheme="teal"
                  onClick={() => openModal(title)}
                >
                  View Details
                </Button>
              </Box>
            ))}
          </Flex>
        </Flex>
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalContent.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody whiteSpace="pre-line">{modalContent.data}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AppointmentDetails;
